import { AppMarkRead } from "./AppMarkRead";
import { AppDemo } from "./AppDemo";
import { AppAccount } from "./AppAccount";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppIcon, AppIcons } from "./AppIcons";
import { WAC_VERSION } from "./AppShared";
import { AppThemeRules, AppThemeView } from "./AppTheme";
import { AppWaLink, AppWaNow } from "./AppWaLink";

// AppTopBar - THE TOP BAR (owner-approved design item 7, 2026-09-30: "Yes, top bar"). The RULES and the MARKUP, habitat-free
// (the Bridge suite reads them); the browser half that mounts it into `<header class="bar">` and keeps it current is
// `AppTopBarRun`.
//
//   wordmark | the chat search (>= 800px in the bar, below it on a phone) | connection dot + "Connected · updated 12 min ago",
//   the settings menu (the theme control, moved here from the footer) and "?" (the keyboard-shortcuts dialog, `AppKeys`).
//
// ⛔ THE STATUS SAYS ONLY WHAT THE APP KNOWS: whether the chat list's live feed (`/live` list mode) is answering, whether the
// browser says it is online, and when the archive (`index.json`) was last rebuilt (`Status`). The page cannot see the
// service's WhatsApp socket, so by itself "Connected" means only "this page is getting live updates".
// ⭐ SINCE 2026-10-01 ("Truthful top bar") THE SERVICE TELLS IT: the list feed carries the subscriber's own WhatsApp link
// state (`AppWaLink`), and `StatusWith` makes ONE line of both - "Connected" then means connected to WhatsApp, and the
// line says "WhatsApp reconnecting…", "WhatsApp not linked", "Service not reachable" ... when that is the truth. A
// service that does not say (older, single-session, `/live` off) leaves `Status` exactly as it was.
// ⛔ NO INLINE STYLE (the CSP): classes and `app.css`; hiding is `hidden`. Nothing here sends (decision 9).

/** What the status line shows. `Kind` "" = nothing to say (no archive loaded). */
export class AppTopStatus {
    public Kind: string = "";
    public Text: string = "";
    public Tip: string = "";
    /**
     * The STATE in one word for a screen reader's live region ("Connected", "Reconnecting", "Offline", "Not live").
     * ⛔ It changes only when the state does - `Text` carries "updated 12 min ago", which moves every minute, and a live
     * region holding it was announced every time (found 2026-09-30).
     */
    public Word: string = "";
    /** `Text` without the state word - read on focus, never announced ("updated 12 min ago"). */
    public When: string = "";
}

/**
 * ⭐ What `StatusWith` knows beyond the feed's health (2026-10-01, "Truthful top bar"): the service's word for the
 * subscriber's own WhatsApp link, the account standing the band shows, and how the page's own feed is failing.
 */
export class AppTopLink {
    /** The link state the service gave (`AppLiveList.WaNow`); null = it does not say. */
    public Wa: AppWaNow | null = null;
    /** The account standing's state word shown now (`AppLiveList.StandingNow().State`). */
    public Standing: string = "ok";
    /** How long the feed's asks have failed in a row (ms; 0 = they have not) - `AppLiveList.FailingMs`. */
    public FailingMs: number = 0;
    /** The service's last answer was "try again in N s" - `AppLiveList.IsWaiting`. */
    public Waiting: boolean = false;
    /** The reader's clock for "at 14:05"; a field so the suite reads fixed words. */
    public Format: (Ms: number) => string = AppWaLink.LocalClock;
}

export class AppTopBarRules {
    /** The live feed's health words (`AppLiveList.Health`). */
    public static readonly Connected: string = "connected";
    public static readonly Reconnecting: string = "reconnecting";
    public static readonly Off: string = "off";
    public static readonly Unknown: string = "unknown";
    /** The line while the feed's first ask is out (`Unknown`): in progress, never "not known". */
    public static readonly CheckingText: string = "Checking WhatsApp…";
    public static readonly CheckingWord: string = "Checking WhatsApp";
    /** The line for a link state word this build has no line of its own for (a newer service). */
    public static readonly ReloadText: string = "Reload to see WhatsApp status";
    /** The status kinds - classes `st-<kind>` in app.css. */
    public static readonly Success: string = "success";
    public static readonly Warning: string = "warning";
    public static readonly Muted: string = "muted";

    /** "just now", "12 min ago", "3 h ago", "2 days ago" - how long ago `Ms` was. Negative (a clock ahead) = "just now". */
    public static Ago = (Ms: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Min: number = Math.floor(Ms / 60000);
            if (isNaN(Min) === true || Min < 1) { return "just now"; }
            if (Min < 60) { return String(Min) + " min ago"; }
            const Hours: number = Math.floor(Min / 60);
            if (Hours < 24) { return String(Hours) + " h ago"; }
            const Days: number = Math.floor(Hours / 24);
            if (Days === 1) { return "1 day ago"; }
            return Days.toLocaleString() + " days ago";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRules.Ago");
            return "";
        }
    };

    /** "updated 12 min ago", or "" when the archive's build time is unknown. */
    public static Updated = (BuiltIso: string, NowMs: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Built: number = Date.parse(nz(BuiltIso));
            if (isNaN(Built) === true) { return ""; }
            return "updated " + AppTopBarRules.Ago(NowMs - Built);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRules.Updated");
            return "";
        }
    };

    /**
     * The status for what the page knows. Offline beats everything (the browser says so); then the live feed: answering =
     * Connected, failing = Reconnecting; no live feed at all (a local copy, a service without `/live`) = only when the
     * archive was updated, muted. Nothing loaded = nothing to say.
     */
    public static Status = (Loaded: boolean, Health: string, Online: boolean, BuiltIso: string, NowMs: number): AppTopStatus => {
        const Out: AppTopStatus = new AppTopStatus();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            if (Loaded !== true) { return Out; }
            const Updated: string = AppTopBarRules.Updated(BuiltIso, NowMs);
            const Archive: string = AppTopBarRules.Archive(Updated);
            if (Online === false) {
                return AppTopBarRules.Make(AppTopBarRules.Muted, "Offline", "This device is offline. You are seeing your chats as they were. " + Archive, "Offline");
            }
            if (Health === AppTopBarRules.Connected) {
                return AppTopBarRules.Make(AppTopBarRules.Success, AppTopBarRules.Join("Connected", Updated),
                    "New messages appear here as they arrive. " + Archive, "Connected");
            }
            if (Health === AppTopBarRules.Reconnecting) {
                return AppTopBarRules.Make(AppTopBarRules.Warning, "Reconnecting…",
                    "Live updates stopped for a moment and WACommander is trying again. " + Archive, "Reconnecting");
            }
            // ⛔ NOTHING IS SHOWN AS "NOT KNOWN" (owner, 2026-10-13). The feed has asked and the service has not answered yet:
            // that is work in progress, said as such, with the neutral dot - not "Not live" (false: live updates ARE on) and
            // not the archive's age posing as a connection state. The first answer replaces it.
            if (Health === AppTopBarRules.Unknown) {
                return AppTopBarRules.Make(AppTopBarRules.Muted, AppTopBarRules.CheckingText, "This page is asking the WACommander"
                    + " service how your WhatsApp connection is doing. " + Archive, AppTopBarRules.CheckingWord);
            }
            let Text: string = "Updated " + Updated.replace("updated ", "");
            if (Updated.length === 0) { Text = "Not live"; }
            return AppTopBarRules.Make(AppTopBarRules.Muted, Text, "Live updates are not on for this page. " + Archive, "Not live");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRules.Status");
            return Out;
        }
    };

    /** The tooltip's last sentence: when the chat list was last rebuilt (`Updated` = `Updated()`'s words, or ""). */
    private static Archive = (Updated: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // No build time in the archive: the sentence is left out (`Make` trims) - never "... is not known".
            if (Updated.length === 0) { return ""; }
            return "Your chat list was last rebuilt " + Updated.replace("updated ", "") + ".";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRules.Archive");
            return "";
        }
    };

    /** The page's feed has failed for less than this: the line keeps the last state the service gave (no flicker). */
    public static readonly FeedDampMs: number = 6000;
    /** A state where nothing is sent or received and the subscriber must act - class `st-danger`. */
    public static readonly Danger: string = "danger";

    /**
     * ⭐ THE TRUTHFUL LINE (owner-picked 2026-10-01): `Status` + what the SERVICE says about the subscriber's own WhatsApp
     * link (`AppWaLink`, carried by the list feed) + the account standing the band shows. ONE line, this precedence:
     *
     *   1. nothing loaded                         -> nothing (as `Status`)
     *   2. the browser is offline                 -> "Offline" (as `Status`)
     *   3. no live feed / not answered yet        -> "Updated ..." / "Not live" (as `Status`)
     *   4. the service does not say (`Wa` null) and no closed standing -> `Status`, byte for byte (an older service, the
     *      single-session path, `/live` off): nothing is claimed about WhatsApp.
     *   5. the feed is failing (after `FeedDampMs`) or silent -> "Service not reachable" - ⛔ NOT the last known WhatsApp
     *      state: a page that cannot reach the service does not know it.
     *   6. the service says "try again in N s"   -> its own word when it sent one that is not `connected`, else
     *      "Waiting for the service…" (no updates are coming, so never "Connected").
     *   7. the band shows a ban / lock / temporary block / refused app version -> "WhatsApp not connected" - ⛔ never
     *      "Connected" beside a red band (those standings hold only while the socket is closed).
     *   8. the service's word                     -> `WaLine`.
     *
     * ⚠ "updated 12 min ago" stays ONLY on "Connected": it says how old the chat list is, which matters when everything
     * works; on every other state the state itself is the news, and the rebuild time moves to the tooltip.
     */
    public static StatusWith = (Loaded: boolean, Health: string, Online: boolean, BuiltIso: string, NowMs: number,
                                Link: AppTopLink): AppTopStatus => {
        if (getCoreUtils().KErrorMode === true) { return new AppTopStatus(); }
        try {
            const Base: AppTopStatus = AppTopBarRules.Status(Loaded, Health, Online, BuiltIso, NowMs);
            if (Loaded !== true || Online === false) { return Base; }
            if (Health !== AppTopBarRules.Connected && Health !== AppTopBarRules.Reconnecting) { return Base; }
            const Closed: boolean = AppWaLink.ClosedStanding(nz(Link.Standing));
            if (Link.Wa === null && Closed === false) { return Base; }
            const Updated: string = AppTopBarRules.Updated(BuiltIso, NowMs);
            const Archive: string = AppTopBarRules.Archive(Updated);
            if (Health === AppTopBarRules.Reconnecting) {
                const Feed: AppTopStatus | null = AppTopBarRules.FeedLine(Link, Archive);
                if (Feed !== null) { return Feed; }
            }
            if (Closed === true) { return AppTopBarRules.WaLine(AppWaLink.Blocked, "", Updated, Archive); }
            const Wa: AppWaNow = Link.Wa as AppWaNow;
            return AppTopBarRules.WaLine(Wa.State, AppWaLink.SinceWords(Wa.Since, NowMs, Link.Format), Updated, Archive);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRules.StatusWith");
            return new AppTopStatus();
        }
    };

    /**
     * The line while the page's feed is NOT answering with a list (rows 5 and 6), or null = show the WhatsApp state after
     * all (a failure younger than `FeedDampMs`, or a "try again" answer that named a state other than `connected`).
     */
    private static FeedLine = (Link: AppTopLink, Archive: string): AppTopStatus | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Link.Waiting === true) {
                if (Link.Wa !== null && (Link.Wa as AppWaNow).State !== AppWaLink.Connected) { return null; }
                return AppTopBarRules.Make(AppTopBarRules.Warning, "Waiting for the service…",
                    "The WACommander service asked this page to try again in a moment, so no updates are arriving right now. "
                    + Archive, "Waiting for the service");
            }
            if (Link.FailingMs > 0 && Link.FailingMs < AppTopBarRules.FeedDampMs) { return null; }
            return AppTopBarRules.Make(AppTopBarRules.Warning, "Service not reachable",
                "This page cannot reach the WACommander service, so it does not know whether WhatsApp is connected. It keeps"
                + " trying. " + Archive, "Service not reachable");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRules.FeedLine");
            return null;
        }
    };

    /**
     * ⭐ THE LINE FOR ONE WHATSAPP LINK STATE (`AppWaLink.States` - `WaLinkLive.test.ts` pins that each has its own).
     * `Since` = `AppWaLink.SinceWords` (" at 14:05" or ""). ⛔ ONLY WHAT THE SERVICE KNOWS, never a forecast: what is
     * true now and what the subscriber can do. A word this build does not know gets the general line.
     */
    public static WaLine = (State: string, Since: string, Updated: string, Archive: string): AppTopStatus => {
        if (getCoreUtils().KErrorMode === true) { return new AppTopStatus(); }
        try {
            if (State === AppWaLink.Connected) {
                return AppTopBarRules.Make(AppTopBarRules.Success, AppTopBarRules.Join("Connected", Updated),
                    "WACommander is connected to WhatsApp, and new messages appear here as they arrive. " + Archive, "Connected");
            }
            if (State === AppWaLink.Reconnecting) { return AppTopBarRules.ReconnectingLine(Since, Archive); }
            if (State === AppWaLink.Starting) {
                return AppTopBarRules.Make(AppTopBarRules.Muted, "WhatsApp starting…", "The WACommander service is starting your"
                    + " WhatsApp connection. New messages do not appear here until it is connected. " + Archive, "WhatsApp starting");
            }
            if (State === AppWaLink.NotLinked) {
                return AppTopBarRules.Make(AppTopBarRules.Danger, "WhatsApp not linked", "WhatsApp is not linked to WACommander,"
                    + " so nothing is sent or received. Use \"Link WhatsApp again\" on this page to link your phone. " + Archive,
                    "WhatsApp not linked");
            }
            if (State === AppWaLink.Blocked) {
                return AppTopBarRules.Make(AppTopBarRules.Danger, "WhatsApp not connected", "WhatsApp is refusing the connection,"
                    + " so nothing is sent or received. The notice under this bar says why. " + Archive, "WhatsApp not connected");
            }
            if (State === AppWaLink.Stopped) {
                return AppTopBarRules.Make(AppTopBarRules.Warning, "WhatsApp not running", "The WACommander service is not running"
                    + " your WhatsApp connection right now - this is at our end. Nothing is sent or received until it is. "
                    + Archive, "WhatsApp not running");
            }
            // ⛔ A WORD THIS BUILD HAS NO LINE FOR (the service is newer than the page). Said as what IS true and what to do:
            // the page is connected to WACommander, and a reload brings the page that can read the service's word.
            // Never "not known" (owner, 2026-10-13).
            return AppTopBarRules.Make(AppTopBarRules.Muted, AppTopBarRules.ReloadText, "This page is connected to WACommander,"
                + " and the service describes your WhatsApp connection in a way this older page cannot show. Reload the page"
                + " to see it. " + Archive, AppTopBarRules.ReloadText);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRules.WaLine");
            return new AppTopStatus();
        }
    };

    /** "WhatsApp reconnecting…" - with when the connection was lost, when the service said. */
    private static ReconnectingLine = (Since: string, Archive: string): AppTopStatus => {
        if (getCoreUtils().KErrorMode === true) { return new AppTopStatus(); }
        try {
            let Tip: string = "WACommander is not connected to WhatsApp right now and keeps trying to connect.";
            if (Since.length > 0) { Tip = "WACommander lost its connection to WhatsApp" + Since + " and is connecting again."; }
            return AppTopBarRules.Make(AppTopBarRules.Warning, "WhatsApp reconnecting…",
                Tip + " New messages do not appear here until it is back. " + Archive, "WhatsApp reconnecting");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRules.ReconnectingLine");
            return new AppTopStatus();
        }
    };

    /** "Connected · updated 12 min ago" ("" parts dropped). */
    private static Join = (First: string, Second: string): string => {
        if (getCoreUtils().KErrorMode === true) { return First; }
        try {
            if (Second.length === 0) { return First; }
            return First + " · " + Second;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRules.Join");
            return First;
        }
    };

    private static Make = (Kind: string, Text: string, Tip: string, Word: string): AppTopStatus => {
        const Out: AppTopStatus = new AppTopStatus();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.Kind = Kind;
            Out.Text = Text;
            Out.Tip = Tip.trim();
            Out.Word = Word;
            // "Connected · updated 12 min ago" -> "updated 12 min ago"; "Updated 3 h ago" (not live) keeps its words; the word alone -> "".
            let When: string = Text;
            if (Text.indexOf(Word) === 0) { When = Text.substring(Word.length).replace(/^ · /, ""); }
            if (When === "…") { When = ""; }
            Out.When = When;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarRules.Make");
            return Out;
        }
    };
}

/** The top bar's markup. ⚠ The ids are the seam `AppTopBarRun` writes into. */
export class AppTopBarView {
    /** ⭐ The notifications item (`AppPush.Item`), handed in by `AppMain` - this file stays habitat-free (the Bridge
     *  typecheck has no DOM lib, and the push client needs it). "" until then. DOCUMENTED EXCEPTION: a seam. */
    public static PushItem: () => string = AppTopBarView.NoItem;

    /** DOCUMENTED EXCEPTION: pure. */
    private static NoItem(): string { return ""; }

    public static readonly SearchId: string = "wac-topsearch";
    public static readonly StatusId: string = "wac-topstatus";
    public static readonly SettingsId: string = "wac-settings";
    public static readonly HelpId: string = "wac-help";

    public static Html = (Theme: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<div class=\"topline\">" + AppTopBarView.Brand()
                + "<div class=\"topsearch\" id=\"" + AppTopBarView.SearchId + "\" hidden></div>"
                // ⛔ ONLY THE STATE WORD IS A LIVE REGION (`.sword`): the visible line ("Connected · updated 12 min ago") moves
                // every minute and was announced each time. On focus a screen reader reads the word and then `.swhen`.
                + "<div class=\"topright\"><span class=\"topstatus\" id=\"" + AppTopBarView.StatusId + "\" tabindex=\"0\" hidden>"
                + AppTopBarView.StatusParts("")
                + "<span class=\"sr sword\" role=\"status\"></span><span class=\"sr swhen\"></span></span>"
                + AppTopBarView.Settings(Theme) + AppTopBarView.Help() + "</div></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarView.Html");
            return "";
        }
    };

    /** The product's name as the bar says it. */
    public static readonly BrandWord: string = "WACommander";

    /**
     * ⭐ THE BRAND (business look wave B, 2026-10-13): the small accent square with the white "W" (`AppIcons.Mark`, the
     * favicon's drawing - an inline SVG of ours, never an `<img>`) and the wordmark in the text colour.
     * DOCUMENTED EXCEPTION: constant markup. ERROR-SUPPRESSED-JUSTIFIED: `AppIcons.Svg` reports its own faults.
     */
    public static Brand = (): string => {
        return "<span class=\"brand\"><span class=\"brandmark\">" + AppIcons.Svg(AppIcons.Mark, 22) + "</span><span class=\"brandword\">"
            + AppTopBarView.BrandWord + "</span>" + AppTopBarView.Version(WAC_VERSION) + "</span>";
    };

    /**
     * The build's version right after the wordmark (owner, 2026-10-19), small and muted, never a link. A screen reader reads
     * "WACommander version 1.1.6.6" (the word "version" is `.sr`). A stamp that is not dotted digits draws nothing.
     */
    public static Version = (Stamp: string): string => {
        if (!/^\d+(\.\d+){1,3}$/.test(Stamp)) { return ""; }
        return "<span class=\"brandver\"><span class=\"sr\">version </span>" + Stamp + "</span>";
    };

    /**
     * What the bar adds around the chat search it hosts: the keycaps of the shortcut that reaches it from anywhere
     * (Ctrl/Cmd+K, `AppKeys`). ⛔ DECORATION ONLY (`aria-hidden`): the field keeps its own `<label>` and `title`; a
     * keycap inside the label would be read out as part of the field's name. Shown at wide widths only (`app.css`).
     */
    public static SearchKeys = (Mac: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Mod: string = "Ctrl";
            if (Mac === true) { Mod = "⌘"; }
            return "<span class=\"findkeys\" aria-hidden=\"true\"><kbd>" + Mod + "</kbd><kbd>K</kbd></span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarView.SearchKeys");
            return "";
        }
    };

    /**
     * What the eye sees of the status line: the dot, the warning icon and the words (`Text`, escaped - "" in the page,
     * where `AppTopBarRun` writes them; the style guide passes each state's words).
     * ⭐ A state that needs attention (`st-warning` / `st-danger`) shows the icon instead of the dot (`app.css`, block
     * `wac-wastate`) - a shape, not colour alone, and on a phone (where the words are for a screen reader only) it is
     * all the eye gets.
     */
    public static StatusParts = (Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<span class=\"dot\" aria-hidden=\"true\"></span><span class=\"sico\" aria-hidden=\"true\">"
                + AppIcons.Svg(AppIcons.Warning, 14) + "</span><span class=\"stxt\" aria-hidden=\"true\">"
                + kHTMLCore.EscapeHtml(nz(Text)) + "</span>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarView.StatusParts");
            return "";
        }
    };

    /** The settings menu: the theme choices (the footer's control, as menu items with words). */
    public static Settings = (Theme: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Items: string[] = AppThemeView.Rows(Theme, (One: string, On: string): string =>
                "<button type=\"button\" class=\"menuitem\" role=\"menuitemradio\" " + AppThemeRules.PickAttr + "=\"" + One
                // ⛔ A `menuitemradio` SAYS ITS STATE WITH `aria-checked` - `aria-pressed` belongs to a toggle BUTTON and is
                // not allowed on this role (found 2026-09-30; `AppThemeRun.Apply` keeps the same rule on a change).
                + "\" aria-checked=\"" + On + "\">" + AppIcons.Svg(AppThemeView.IconOf(One), 16)
                + "<span>" + kHTMLCore.EscapeHtml(AppThemeView.Words(One)) + "</span></button>");
            return AppTopBarView.Menu(AppTopBarView.SettingsId, "Settings", AppIcons.Settings, "menu",
                "<p class=\"menuhead\" id=\"wac-themehead\">Theme</p><div role=\"group\" aria-labelledby=\"wac-themehead\">"
                + Items.join("") + "</div>"
                // (2026-10-14) Read receipts out - a checkbox item (`AppMarkRead`), default on.
                + "<p class=\"menuhead\" id=\"wac-readhead\">WhatsApp</p><div role=\"group\" aria-labelledby=\"wac-readhead\">"
                + AppMarkRead.Item() + AppTopBarView.PushItem() + AppDemo.Item() + AppDemo.MenuPicks() + "</div>"
                // (2026-11-02, owner) "Close my account" lives here, not in the footer.
                + "<p class=\"menuhead\" id=\"wac-accthead\">Account</p><div role=\"group\" aria-labelledby=\"wac-accthead\">"
                + "<a class=\"menuitem\" role=\"menuitem\" href=\"#" + AppAccount.LeaveRoute + "\"><span>Close my account</span></a></div>");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarView.Settings");
            return "";
        }
    };

    /** The attribute `AppKeys` listens for (one delegated click): the element carrying it opens the shortcuts dialog. */
    public static readonly KeysHelpAttr: string = "data-wac-keys-help";

    /**
     * "?" - opens the keyboard-shortcuts dialog (`AppKeys`' native modal <dialog>: the page inert, Esc closes, focus comes
     * back to this button). ⚠ A PLAIN BUTTON, NOT AN `AppMenus` POPOVER: the dialog is the one help surface (the `?` key and
     * the palette open the same one), so a second, shorter popover here would say less and disagree with it.
     */
    public static Help = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Words: string = kHTMLCore.EscapeAttr("Help and keyboard shortcuts");
            return "<button type=\"button\" class=\"btn ghost icon sm topbtn\" id=\"" + AppTopBarView.HelpId + "\" "
                + AppTopBarView.KeysHelpAttr + " aria-haspopup=\"dialog\" aria-label=\"" + Words + "\" title=\"" + Words + "\">"
                + AppIcons.Svg(AppIcons.Help, 18) + "</button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarView.Help");
            return "";
        }
    };

    /** A menu button and its popover (`AppMenus` gives them their behaviour). */
    public static Menu = (Id: string, Label: string, Icon: AppIcon, Role: string, Body: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Words: string = kHTMLCore.EscapeAttr(Label);
            return "<div class=\"topmenu\"><button type=\"button\" class=\"btn ghost icon sm topbtn\" data-wac-menu=\"" + Id
                + "\" aria-haspopup=\"" + Role + "\" aria-expanded=\"false\" aria-controls=\"" + Id + "\" aria-label=\"" + Words
                + "\" title=\"" + Words + "\">" + AppIcons.Svg(Icon, 18) + "</button><div class=\"menupop\" id=\"" + Id
                + "\" role=\"" + Role + "\" aria-label=\"" + Words + "\" hidden>" + Body + "</div></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTopBarView.Menu");
            return "";
        }
    };
}
